import { useSelector as reduxUseSelector } from 'react-redux';
import { pick, isEqual } from 'lodash';

import * as PROPERTIES from 'data/nodeProperties';
import usePreviousHook from 'containers/App/hooks/usePrevious';
import {
  makeSelectSurveyType,
  makeSurveyIsUnderEdit,
} from 'containers/Survey/selectors';
import {
  SURVEY_TYPE_INTERNAL,
  SURVEY_TYPE_PEEQ,
} from 'containers/Survey/constants';

import {
  featureCleaner,
  getActiveScreeningQuestionCount,
} from 'containers/TargetingPage/helpers';
import {
  makeSelectScreeningQuestions,
  makeSelectSelectedCriteriaPerAudience,
  makeSelectTargetingVersion,
  makeSelectHasFetched,
} from 'containers/TargetingPage/selectors';

let targetingData;
let previousSurveyId;
function useGetTargetingData(
  { surveyId, overrideSurveyType },
  { useSelector = reduxUseSelector, usePrevious = usePreviousHook } = {},
) {
  const selectedCriteriaPerAudience = useSelector(
    makeSelectSelectedCriteriaPerAudience,
  );
  const targetingEditVersion = useSelector(makeSelectTargetingVersion);
  const screeningQuestions = useSelector(makeSelectScreeningQuestions);
  const surveyIsUnderEdit = useSelector(makeSurveyIsUnderEdit);
  const surveyType = useSelector(makeSelectSurveyType);

  const prevselectedCriteriaPerAudience = usePrevious(
    selectedCriteriaPerAudience,
  );
  const prevTargetingEditVersion = usePrevious(targetingEditVersion);
  const prevScreeningQuestions = usePrevious(screeningQuestions);

  const surveyIsInternal = surveyType === SURVEY_TYPE_INTERNAL;

  const hasFetched = useSelector(makeSelectHasFetched);

  if (
    surveyType === SURVEY_TYPE_PEEQ ||
    overrideSurveyType === SURVEY_TYPE_PEEQ
  ) {
    return {};
  }

  if (!hasFetched) {
    return { shouldUpdateTargeting: false };
  }

  if (previousSurveyId !== surveyId) {
    targetingData = undefined;
    previousSurveyId = surveyId;
  }

  const shouldUpdateTargeting =
    targetingEditVersion !== prevTargetingEditVersion &&
    targetingEditVersion !== -1 &&
    prevTargetingEditVersion !== -1;

  if (!shouldUpdateTargeting && targetingData) {
    return {
      ...targetingData,
      shouldUpdateTargeting,
      feasibilityAffected: false,
      costAffected: false,
      previousVersion: prevTargetingEditVersion,
      version: targetingEditVersion,
    };
  }

  const cleanFeaturesPerAudience = selectedCriteriaPerAudience.map(
    ({ criteria, ...audience }) => ({
      ...audience,
      features: featureCleaner(criteria),
    }),
  );

  let cleanPreviousFeaturesPerAudience = [];
  if (prevselectedCriteriaPerAudience) {
    cleanPreviousFeaturesPerAudience = prevselectedCriteriaPerAudience.map(
      ({ criteria, ...audience }) => ({
        ...audience,
        features: featureCleaner(criteria),
      }),
    );
  }

  const cleanScreeningQuestions = screeningQuestions.map((question) => {
    const backendSupportedData = pick(question, [
      PROPERTIES.PROPERTY_ID,
      PROPERTIES.PROPERTY_REF_ID,
      PROPERTIES.PROPERTY_PROMPT,
      PROPERTIES.PROPERTY_PROMPT_HTML,
      PROPERTIES.PROPERTY_IS_SCREENING,
      PROPERTIES.PROPERTY_TYPE,
      PROPERTIES.PROPERTY_SHUFFLE,
      PROPERTIES.PROPERTY_ANSWERS,
      PROPERTIES.PROPERTY_HAS_QUOTAS,
      PROPERTIES.PROPERTY_MEDIA,
    ]);

    const { answers } = backendSupportedData;
    if (answers) {
      backendSupportedData.answers = answers.map((answer) => {
        const { hasImage } = answer;
        if (!hasImage) {
          return {
            ...answer,
            imagePath: undefined,
            mediaType: undefined,
          };
        }
        return answer;
      });
    }
    return backendSupportedData;
  });

  const feasibilityAffected =
    !surveyIsInternal &&
    !isEqual(cleanFeaturesPerAudience, cleanPreviousFeaturesPerAudience);

  const costAffected =
    !surveyIsInternal &&
    (feasibilityAffected ||
      getActiveScreeningQuestionCount(screeningQuestions) !==
        getActiveScreeningQuestionCount(prevScreeningQuestions));

  targetingData = {
    targeting: {
      audiences: cleanFeaturesPerAudience,
      screeningQuestions: cleanScreeningQuestions,
    },
    previousVersion: prevTargetingEditVersion,
    version: targetingEditVersion,
    feasibilityAffected,
    costAffected,
    isRevision: surveyIsUnderEdit,
    shouldUpdateTargeting,
  };

  return targetingData;
}

export default useGetTargetingData;
